/* Help — annotation 9 on the client's wireframe.
 *
 * Their description, in two steps:
 *
 *   "There must be a popup opened on click of Help with below options…
 *    Once the user has selected any of the above option(s), he must be shown
 *    with a list of available PR's which are relevant to the help option
 *    selected. So that he can select the PR's which he is interested and
 *    contact\help them."
 *
 * So: PICK what you can offer, then SEE who needs it. Two panes in one dialog
 * rather than two screens, because the second is meaningless without the first
 * and someone will want to go back and adjust.
 *
 * "Contact" posts a comment on the request naming the offer — see
 * docs/CLIENT_TERMINOLOGY.md for why that rather than a private message.
 */
import { useEffect, useState } from 'react'
import { ArrowLeft, Check, HandHeart, Loader2 } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import {
  useHelpMatches,
  useHelpOptions,
  useMyOffers,
  useSetOffers,
} from '@/api/queries'
import { cn, timeAgo } from '@/lib/utils'
import { OfferHelpDialog } from './PrayerCard'
import type { PrayerRequest } from '@/types/api'
import { useNavigate } from 'react-router-dom'

export function HelpDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [step, setStep] = useState<'pick' | 'matches'>('pick')
  const [selected, setSelected] = useState<string[]>([])

  const options = useHelpOptions()
  const myOffers = useMyOffers()
  const setOffers = useSetOffers()

  // Start from what they already offered, so the popup reflects reality rather
  // than asking the same question again.
  useEffect(() => {
    if (myOffers.data) setSelected(myOffers.data.kinds)
  }, [myOffers.data])

  function toggle(kind: string) {
    setSelected((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind],
    )
  }

  /* BROWSING IS FREE. SAVING IS DELIBERATE.
   *
   * Ticking used to write straight to help_offers — which DELETES and
   * re-inserts — so somebody looking at "who needs groceries today" silently
   * replaced their standing offers, and unticking one to narrow a search
   * quietly withdrew it.
   *
   * Now the ticks only filter. What a person has actually committed to is a
   * separate act, below. */
  function seeMatches() {
    if (!selected.length) return
    // A SCREEN, not a second view inside this dialog. As a modal step it had to
    // be a five-row digest ending in "go and look on the Prayer page", which is
    // a strange thing to tell somebody who just said they can help. The kinds
    // ride in the URL so the page survives a refresh and can be shared.
    onClose()
    navigate(`/prayer/help?kinds=${selected.join(',')}`)
  }

  /** The ticks as they stand, saved as this person's standing offers. */
  function saveOffers() {
    setOffers.mutate(selected)
  }

  const saved = myOffers.data?.kinds ?? []
  const offersChanged =
    selected.length !== saved.length || selected.some((k) => !saved.includes(k))

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={step === 'pick' ? 'How can you help?' : 'People who need this'}
      width="560px"
      footer={
        step === 'pick' ? (
          <div className="space-y-s2">
            <Button
              variant="primary"
              size="lg"
              full
              onClick={seeMatches}
              disabled={!selected.length}
            >
              {selected.length
                ? `See who needs this (${selected.length})`
                : 'Choose at least one'}
            </Button>

            {/* The standing commitment, kept separate from browsing. Only
                offered when the ticks differ from what is already saved —
                a button that would change nothing is noise. */}
            {offersChanged ? (
              <Button
                variant="secondary"
                size="lg"
                full
                onClick={saveOffers}
                loading={setOffers.isPending}
              >
                {selected.length
                  ? 'Always tell me about these'
                  : 'Stop telling me about any of these'}
              </Button>
            ) : saved.length ? (
              <p className="text-center text-sm text-ink-2">
                You are told when someone needs {saved.length === 1 ? 'this' : 'these'}.
              </p>
            ) : null}
          </div>
        ) : (
          <Button
            variant="secondary"
            size="lg"
            full
            onClick={() => setStep('pick')}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Change what you offer
          </Button>
        )
      }
    >
      {step === 'pick' ? (
        <div className="p-s4">
          <p className="mb-s3 text-base text-ink-2">
            Choose what you are able to offer, and see who needs it.
          </p>

          {options.isLoading ? (
            <div className="flex justify-center py-s6">
              <Loader2
                className="h-5 w-5 animate-spin text-ink-2"
                aria-label="Loading"
              />
            </div>
          ) : null}

          <ul className="space-y-s2">
            {options.data?.map(({ kind, label }) => {
              const on = selected.includes(kind)
              return (
                <li key={kind}>
                  <button
                    onClick={() => toggle(kind)}
                    aria-pressed={on}
                    className={cn(
                      'flex w-full items-center gap-s3 rounded-md border px-s4 py-s3 text-left text-md font-semibold transition-colors',
                      on
                        ? 'border-brand bg-brand-softer text-brand'
                        : 'border-line text-ink hover:bg-hover',
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-5 w-5 shrink-0 place-items-center rounded-sm border',
                        on ? 'border-brand bg-brand text-white' : 'border-line',
                      )}
                      aria-hidden
                    >
                      {on ? <Check className="h-3.5 w-3.5" /> : null}
                    </span>
                    {label}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ) : (
        <MatchList kinds={selected} />
      )}
    </Modal>
  )
}

/** A DIGEST, not an endless list.
 *
 *  The client's document describes showing "a list of available PR's which are
 *  relevant to the help option selected". At a million requests that list is a
 *  wall of strangers' needs, and the reader helps nobody — so this shows the
 *  most recent handful and sends people back to the Prayer page, where Offer
 *  help sits on each card next to the need it belongs to.
 */
const DIGEST = 5

function MatchList({ kinds }: { kinds: string[] }) {
  const matches = useHelpMatches(kinds)
  const all = matches.data?.pages.flatMap((p) => p.items) ?? []

  /* TWO GROUPS, because they are not the same thing.
   *
   * A request the author tagged "a job referral" is a real match for somebody
   * offering to hire. An untagged one is a maybe — it is included because a
   * need nobody labelled is still a need, but presenting the two together made
   * the feature look broken: ticking "I can hire" showed prayers about exams
   * with nothing to say why they were there. */
  const tagged = all.filter((r) => r.help_kind)
  const untagged = all.filter((r) => !r.help_kind)
  const items = tagged.slice(0, DIGEST)
  const others = untagged.slice(0, Math.max(0, DIGEST - items.length))

  if (matches.isLoading) {
    return (
      <div className="flex justify-center py-s8">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </div>
    )
  }

  if (!items.length && !others.length) {
    return (
      <div className="px-s5 py-s8 text-center">
        <HandHeart className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
        <p className="mt-s3 text-md font-bold">Nobody needs this right now</p>
        <p className="mt-1 text-base text-ink-2">
          Nobody in your church has asked for this yet.
        </p>
      </div>
    )
  }

  return (
    <div>
      {items.length ? (
        <ul className="divide-y divide-line-2">
          {items.map((request) => (
            <MatchRow key={request.id} request={request} />
          ))}
        </ul>
      ) : null}

      {/* Said plainly rather than mixed in: these are not matches, they are
          requests whose author did not say what would help. */}
      {others.length ? (
        <>
          <p className="border-t border-line-2 bg-surface-2 px-s4 py-s2 text-sm font-semibold text-ink-2">
            {items.length
              ? 'Others who may need help'
              : 'Nobody asked for this directly, but these may need help'}
          </p>
          <ul className="divide-y divide-line-2">
            {others.map((request) => (
              <MatchRow key={request.id} request={request} />
            ))}
          </ul>
        </>
      ) : null}

      {all.length > DIGEST || matches.hasNextPage ? (
        <p className="border-t border-line-2 p-s4 text-center text-base text-ink-2">
          More people need this. Offer help from any Prayer Request on the
          Prayer page.
        </p>
      ) : null}
    </div>
  )
}

/** One matching request, with the same Offer help dialog the card uses.
 *
 *  This used to have its own cut-down form: a bare textarea that posted the
 *  offer with `help_kind: request.help_kind` — the AUTHOR's tag. On an
 *  untagged request, which is most of them and is exactly what appears under
 *  "Others who may need help", the offer went out with no kind at all, so it
 *  read as an ordinary comment rather than an offer of help.
 */
export function MatchRow({ request }: { request: PrayerRequest }) {
  const [offering, setOffering] = useState(false)

  return (
    <li className="p-s4">
      <div className="flex items-start gap-s3">
        <Avatar
          name={request.author.name}
          src={request.author.avatar_url}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <div className="text-md font-semibold">{request.author.name}</div>
          <div className="text-sm text-ink-2">{timeAgo(request.created_at)}</div>
          <p className="mt-1 line-clamp-3 text-base leading-normal">
            {request.body}
          </p>
        </div>
      </div>

      <Button
        variant="secondary"
        size="sm"
        className="mt-s3"
        onClick={() => setOffering(true)}
      >
        <HandHeart className="h-4 w-4" aria-hidden />
        Offer help
      </Button>

      {offering ? (
        <OfferHelpDialog request={request} onClose={() => setOffering(false)} />
      ) : null}
    </li>
  )
}
